import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate, formatEuro } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { SummaryCard } from '../components/tresorerie/SummaryCard';
import { StatutPaiementBadge } from '../components/ui/Badge';
import { PaiementClientModal } from '../components/tresorerie/PaiementClientModal';

export function TresorerieEncaissementsPage(): JSX.Element {
  const [statutFilter, setStatutFilter] = useState<string>('');
  const [paiementFor, setPaiementFor] = useState<{
    venteId: number;
    montantCentimes: number;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['encaissements', { statut: statutFilter }],
    queryFn: () =>
      api.tresorerie.encaissements(statutFilter === '' ? {} : { statut_effectif: statutFilter }),
  });
  const { data: stats } = useQuery({
    queryKey: ['encaissements-stats'],
    queryFn: () => api.tresorerie.encaissementsStats(),
  });

  const items = data?.items ?? [];

  return (
    <div className="max-w-7xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-100">Encaissements / Impayés</h1>
        <p className="text-xs text-slate-500 mt-1">
          Ventes non soldées, calcul du statut effectif côté serveur.
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          label="Total impayés"
          value={formatEuro(stats?.total_impayes_centimes ?? 0)}
          tone="warning"
        />
        <SummaryCard
          label="Impayés en retard"
          value={formatEuro(stats?.total_impayes_en_retard_centimes ?? 0)}
          tone={(stats?.total_impayes_en_retard_centimes ?? 0) > 0 ? 'negative' : 'neutral'}
        />
        <SummaryCard label="Ventes ouvertes" value={String(stats?.nb_ventes_ouvertes ?? 0)} />
        <SummaryCard
          label="Ventes en retard"
          value={String(stats?.nb_ventes_en_retard ?? 0)}
          tone={(stats?.nb_ventes_en_retard ?? 0) > 0 ? 'negative' : 'neutral'}
        />
      </div>

      <div className="flex items-center gap-3">
        <Select
          label="Statut effectif"
          value={statutFilter}
          onChange={(e): void => setStatutFilter(e.target.value)}
          options={[
            { value: '', label: 'Tous' },
            { value: 'Payee', label: 'Payée' },
            { value: 'En_attente', label: 'En attente' },
            { value: 'En_retard', label: 'En retard' },
          ]}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/50 border-b border-slate-800">
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 px-3">N° fac.</th>
                <th className="py-2 px-3">Client</th>
                <th className="py-2 px-3 text-right">TTC</th>
                <th className="py-2 px-3 text-right">Payé</th>
                <th className="py-2 px-3 text-right">Restant</th>
                <th className="py-2 px-3">Émission</th>
                <th className="py-2 px-3">Échéance</th>
                <th className="py-2 px-3">Statut</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.vente_id} className="border-b border-slate-800/50">
                  <td className="py-2 px-3">
                    <Link
                      to={`/ventes/${String(it.vente_id)}`}
                      className="text-floryon-green hover:underline"
                    >
                      {it.numero_facture_henrri ?? `#${String(it.vente_id)}`}
                    </Link>
                  </td>
                  <td className="py-2 px-3">
                    <Link
                      to={`/contacts/${String(it.contact_id)}`}
                      className="text-slate-100 hover:underline"
                    >
                      {it.contact_societe ?? it.contact_nom}
                    </Link>
                  </td>
                  <td className="py-2 px-3 text-right">{formatEuro(it.total_ttc_centimes)}</td>
                  <td className="py-2 px-3 text-right">{formatEuro(it.paye_centimes)}</td>
                  <td className="py-2 px-3 text-right">{formatEuro(it.restant_centimes)}</td>
                  <td className="py-2 px-3 text-slate-400">{formatDate(it.date_emission)}</td>
                  <td className="py-2 px-3 text-slate-400">{formatDate(it.date_echeance)}</td>
                  <td className="py-2 px-3">
                    <StatutPaiementBadge statut={it.statut_effectif} />
                  </td>
                  <td className="py-2 px-3 text-right">
                    {it.statut_effectif !== 'Payee' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(): void =>
                          setPaiementFor({
                            venteId: it.vente_id,
                            montantCentimes: it.restant_centimes,
                          })
                        }
                      >
                        Payer
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-sm text-slate-500">
                    Aucune vente non soldée.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {paiementFor !== null && (
        <PaiementClientModal
          isOpen={true}
          onClose={(): void => setPaiementFor(null)}
          venteId={paiementFor.venteId}
          echeanceId={null}
          defaultMontantCentimes={paiementFor.montantCentimes}
        />
      )}
    </div>
  );
}
